import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { absoluteUrl } from "@/lib/site";
import { formatMoney, tierPriceFor } from "@/lib/bkls";
import { buildSections, type MarketplaceSection } from "@/lib/bkls-marketplace";
import { BeatCard } from "@/components/beats/BeatCard";
import {
  beatCollectionsQuery,
  beatsCatalogQuery,
  beatsEngagementQuery,
  beatsSettingsQuery,
  beatsTiersQuery,
} from "@/lib/bkls-queries";
import { track } from "@/lib/analytics";

// Re-exported so existing route modules keep a single import site.
export { beatsCatalogQuery, beatsSettingsQuery, beatsTiersQuery } from "@/lib/bkls-queries";

const TITLE = "Beat Licensing | Buddha KA Original Production Marketplace";
const DESCRIPTION =
  "License original Buddha KA production — conscious hip-hop, zen, boom bap and cinematic instrumentals with clear non-exclusive tiers, permanent written terms, and custom licensing for film, brand and sync.";

export const Route = createFileRoute("/beats/")({
  loader: async ({ context }) => {
    // Every query this route renders must be hydrated from the loader,
    // otherwise SSR resolves it and the client's first render does not —
    // which surfaces as a hydration mismatch.
    await Promise.all([
      context.queryClient.ensureQueryData(beatsCatalogQuery),
      context.queryClient.ensureQueryData(beatsSettingsQuery),
      context.queryClient.ensureQueryData(beatsTiersQuery),
      context.queryClient.ensureQueryData(beatsEngagementQuery),
      context.queryClient.ensureQueryData(beatCollectionsQuery),
    ]);
    return null;
  },
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl("/beats") },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/beats") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: TITLE,
          description: DESCRIPTION,
          url: absoluteUrl("/beats"),
          isPartOf: { "@type": "WebSite", name: "Buddha KA · KAfrequency", url: absoluteUrl("/") },
        }),
      },
    ],
  }),
  component: BeatsMarketplace,
});

function BeatsMarketplace() {
  const settings = useQuery(beatsSettingsQuery);
  const beats = useQuery(beatsCatalogQuery);
  const tiers = useQuery(beatsTiersQuery);
  const engagement = useQuery(beatsEngagementQuery);
  const collections = useQuery(beatCollectionsQuery);

  const rows = beats.data ?? [];
  const sections: MarketplaceSection[] = buildSections(rows, engagement.data ?? []);
  const populatedCollections = (collections.data ?? []).filter((c) => c.beatCount > 0);

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
      <header className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">The Sound Chamber</p>
        <h1 className="mt-4 font-serif text-5xl leading-tight sm:text-6xl">Beat Licensing.</h1>
        <p className="mt-6 text-muted-foreground">
          Original production from the KAfrequency world, licensed with terms that stay readable and stay
          permanent. Every purchase grants a licence — never a transfer of copyright.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/beats/catalog"
            className="rounded-full border border-gold px-5 py-2.5 text-sm font-medium text-gold-luminous transition-all hover:bg-gold hover:text-obsidian"
          >
            Browse the catalog
          </Link>
          <Link
            to="/beats/collections"
            className="rounded-full border hairline px-5 py-2.5 text-sm text-foreground/80 transition-colors hover:border-gold hover:text-gold"
          >
            Collections
          </Link>
          <Link
            to="/beats/license"
            className="rounded-full border hairline px-5 py-2.5 text-sm text-foreground/80 transition-colors hover:border-gold hover:text-gold"
          >
            How licensing works
          </Link>
          <Link
            to="/beats/custom"
            className="rounded-full border hairline px-5 py-2.5 text-sm text-foreground/80 transition-colors hover:border-gold hover:text-gold"
          >
            Request a custom licence
          </Link>
        </div>
      </header>

      {settings.data && !settings.data.checkout_enabled && (
        <p className="mt-10 rounded-2xl border hairline bg-charcoal p-5 text-sm text-muted-foreground">
          Instant licence checkout is not open yet. Licences are currently issued directly — send a request and
          you will get written terms and a quote.
        </p>
      )}

      {beats.isLoading ? (
        <p className="mt-16 text-sm text-muted-foreground">Loading the catalog…</p>
      ) : beats.isError ? (
        <p role="alert" className="mt-16 rounded-2xl border hairline bg-charcoal p-6 text-sm text-red-400">
          The catalog could not be loaded right now. Please refresh, or send a custom licensing request.
        </p>
      ) : rows.length === 0 ? (
        <EmptyChamber />
      ) : (
        sections.map((section) => (
          <section key={section.key} className="mt-16" aria-labelledby={`sec-${section.key}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <h2 id={`sec-${section.key}`} className="font-serif text-3xl">
                  {section.title}
                </h2>
                <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{section.description}</p>
              </div>
              {section.filter && (
                <Link
                  to="/beats/catalog"
                  search={section.filter.genre ? { genre: section.filter.genre } : {}}
                  className="text-xs text-gold hover:text-gold-luminous"
                >
                  View all →
                </Link>
              )}
            </div>
            <ul className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {section.beats.map((beat) => (
                <li key={beat.id}>
                  <BeatCard
                    beat={beat}
                    tiers={tiers.data ?? []}
                    onSelect={() => track("beat_card_click", { slug: beat.slug, section: section.key })}
                  />
                </li>
              ))}
            </ul>
          </section>
        ))
      )}

      {populatedCollections.length > 0 && (
        <section className="mt-20" aria-labelledby="collections-heading">
          <h2 id="collections-heading" className="font-serif text-3xl">
            Curated collections
          </h2>
          <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {populatedCollections.map((c) => (
              <li key={c.slug}>
                <Link
                  to="/beats/collections/$slug"
                  params={{ slug: c.slug }}
                  className="block rounded-2xl border hairline bg-charcoal p-6 transition-colors hover:border-gold"
                >
                  <h3 className="font-serif text-2xl text-ivory">{c.name}</h3>
                  {c.tagline && <p className="mt-2 text-sm text-muted-foreground">{c.tagline}</p>}
                  <p className="mt-4 text-xs uppercase tracking-[0.2em] text-gold/80">
                    {c.beatCount} {c.beatCount === 1 ? "beat" : "beats"}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-20" aria-labelledby="tiers-heading">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Licence tiers</p>
        <h2 id="tiers-heading" className="mt-2 font-serif text-3xl">
          Ways to carry the sound.
        </h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {(tiers.data ?? []).map((tier) => {
            const price = tierPriceFor(tier);
            return (
              <article key={tier.key} className="rounded-2xl border hairline bg-charcoal p-6">
                <h3 className="font-serif text-xl text-gold-luminous">{tier.name}</h3>
                <p className="mt-2 min-h-10 text-sm text-muted-foreground">{tier.tagline}</p>
                <p className="mt-4 text-sm text-ivory">
                  {tier.is_quote_only ? "By quote" : price ? formatMoney(price, tier.currency) : "Price on request"}
                </p>
                <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground">
                  <li>Formats: {tier.file_formats.join(", ") || "—"}</li>
                  {tier.stream_limit && <li>Streams: {tier.stream_limit}</li>}
                  {tier.video_limit && <li>Video: {tier.video_limit}</li>}
                  <li>Term: {tier.term}</li>
                  <li>Territory: {tier.territory}</li>
                </ul>
              </article>
            );
          })}
        </div>
        <Link to="/beats/license" className="mt-6 inline-flex text-sm text-gold hover:text-gold-luminous">
          Full terms, restrictions and FAQ →
        </Link>
      </section>
    </div>
  );
}

function EmptyChamber() {
  return (
    <div className="mt-16 rounded-2xl border hairline bg-charcoal p-10 text-center">
      <p className="font-serif text-2xl text-ivory">The chamber is still.</p>
      <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
        No beats have been released for licensing yet. When the first is published it appears here — custom
        licensing is open in the meantime.
      </p>
      <Link
        to="/beats/custom"
        className="mt-6 inline-flex rounded-full border border-gold px-5 py-2.5 text-sm text-gold-luminous transition-all hover:bg-gold hover:text-obsidian"
      >
        Request a custom licence
      </Link>
    </div>
  );
}
